import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import ISupplyLinkPage from './components/supply/ISupplyLinkPage';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/layout/Layout';
import Login from './components/auth/Login';
import Dashboard from './components/dashboard/Dashboard';
import AssetsList from './components/assets/AssetsList';
import AssetImportExport from './components/assets/AssetImportExport';
import FinancialYearReview from './components/assets/FinancialYearReview';
import LabelPrint from './components/assets/LabelPrint';
import VendorImportExport from './components/vendors/VendorImportExport';
import AssetForm from './components/assets/AssetForm';
import AssetDetail from './components/assets/AssetDetail';
import AssetPublicView from './components/assets/AssetPublicView';
import AssetProfilePage from './components/assets/AssetProfilePage';
import AssetProfileModal from './components/assets/AssetProfileModal';
import WorkOrdersList from './components/workorders/WorkOrdersList';
import WorkOrderForm from './components/workorders/WorkOrderForm';
import WorkOrderDetail from './components/workorders/WorkOrderDetail';
import MaintenancePage from './components/maintenance/MaintenancePage';
import CompliancePage from './components/compliance/CompliancePage';
import ReportsPage from './components/reports/ReportsPage';
import SparesPage from './components/spares/SparesPage';
import AuditPage from './components/audit/AuditPage';
import SettingsPage from './components/settings/SettingsPage';
import IPurchasePage from './components/ipurchase/IPurchasePage';
import SetupPage from './components/setup/SetupPage';
import VendorPage from './components/vendors/VendorPage';
import PWAInstallBanner from './components/common/PWAInstallBanner';
import AssetMobileProfile from './components/assets/AssetMobileProfile';
import './index.css';

/* ── Mobile pages ───────────────────────────────────────── */
import MobileDashboard   from './components/mobile/MobileDashboard';
import MobileAssets      from './components/mobile/MobileAssets';
import MobileWorkOrders  from './components/mobile/MobileWorkOrders';
import MobileQRScanner   from './components/mobile/MobileQRScanner';
import MobileProfile     from './components/mobile/MobileProfile';
import { useMobile }     from './hooks/useMobile';

import './index.css';

/* ── Private route guard ────────────────────────────────── */
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
                  height:'100vh', flexDirection:'column', gap:16 }}>
      <div style={{ width:36, height:36, background:'#1a56db', borderRadius:9,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    color:'#fff', fontWeight:800 }}>iA</div>
      <div style={{ fontSize:13, color:'#94a3b8' }}>Loading…</div>
    </div>
  );
  return user ? children : <Navigate to="/login" replace />;
};

/* ── Coming soon placeholder ────────────────────────────── */
const ComingSoon = ({ name }) => (
  <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
                justifyContent:'center', height:'60vh', gap:12, color:'#94a3b8' }}>
    <div style={{ fontSize:48 }}>🚧</div>
    <div style={{ fontSize:20, fontWeight:700, color:'#1e2a3b' }}>{name}</div>
    <div style={{ fontSize:14 }}>Coming soon</div>
  </div>
);

/* ── Auto-redirect mobile users to /mobile ──────────────── */
function MobileRedirect() {
  const isMobile = useMobile();
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useAuth();

  useEffect(() => {
    if (!user) return;                          // not logged in, skip
    if (!isMobile) return;                      // desktop, skip
    const p = location.pathname;
    if (p.startsWith('/mobile')) return;        // already on mobile page
    if (p.startsWith('/a/'))     return;        // QR scan page, skip
    if (p === '/login')          return;        // login page, skip
    navigate('/mobile', { replace: true });
  }, [isMobile, location.pathname, user, navigate]);

  return null;
}

/* ── App ─────────────────────────────────────────────────── */
export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Toaster
            position="top-right"
            toastOptions={{
              style: { fontFamily: 'DM Sans, sans-serif', fontSize: 13 },
              success: { iconTheme: { primary: '#22c55e' } }
            }}
          />

          {/* Auto-redirect mobile users — must be inside BrowserRouter */}
          <MobileRedirect />

          <Routes>
            {/* ── Auth ──────────────────────────────────── */}
            <Route path="/login" element={<Login />} />
            {/* ── Mobile pages (no Layout wrapper) ─────── */}
            <Route path="/mobile"             element={<PrivateRoute><MobileDashboard /></PrivateRoute>} />
            <Route path="/mobile/assets"      element={<PrivateRoute><MobileAssets /></PrivateRoute>} />
            <Route path="/mobile/work-orders" element={<PrivateRoute><MobileWorkOrders /></PrivateRoute>} />
            <Route path="/mobile/scan"        element={<PrivateRoute><MobileQRScanner /></PrivateRoute>} />
            <Route path="/mobile/profile"     element={<PrivateRoute><MobileProfile /></PrivateRoute>} />
          <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard"            element={<Dashboard />} />
            <Route path="assets"               element={<AssetsList />} />
	     <Route path="assets/import-export" element={<AssetImportExport />} />
	      <Route path="assets/financial-year-review"  element={<FinancialYearReview />} />
<Route path="assets/label-print"              element={<LabelPrint />} />
            <Route path="assets/new"           element={<AssetProfilePage />} />
            <Route path="assets/:id/profile"   element={<AssetProfilePage />} />
            <Route path="assets/:id"           element={<AssetDetail />} />
            <Route path="assets/:id/edit"      element={<AssetForm />} />
            <Route path="work-orders"          element={<WorkOrdersList />} />
            <Route path="work-orders/new"      element={<WorkOrderForm />} />
            <Route path="work-orders/:id"      element={<WorkOrderDetail />} />
            <Route path="work-orders/:id/edit" element={<WorkOrderForm />} />
            <Route path="maintenance"          element={<MaintenancePage />} />
              <Route path="maintenance/new"      element={<WorkOrderForm />} />
            <Route path="compliance"           element={<CompliancePage />} />
            <Route path="reports"              element={<ReportsPage />} />
            <Route path="spares"               element={<SparesPage />} />
            <Route path="audit"                element={<AuditPage />} />
            <Route path="settings"             element={<SettingsPage />} />
            <Route path="setup"                element={<SetupPage />} />
            <Route path="ipurchase"            element={<IPurchasePage />} />
              <Route path="vendors/import-export" element={<VendorImportExport />} />
            <Route path="chats"                element={<ComingSoon name="iChats" />} />
            <Route path="events"               element={<ComingSoon name="iEvents" />} />
            <Route path="incidents"            element={<ComingSoon name="iIncidents" />} />
            <Route path="supply"               element={<ISupplyLinkPage />} />
            <Route path="vendors"              element={<VendorPage />} />
          </Route>
          <Route path="/a/:assetCode" element={<AssetPublicView />} />
          <Route path="/a/:assetCode/profile" element={<AssetMobileProfile />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <PWAInstallBanner /> 
      </ThemeProvider>
    </AuthProvider>
  );
}
